import React from 'react'
import { X, Calendar, Clock, DollarSign, User, CheckCircle, AlertCircle } from 'lucide-react'
import type { Booking } from '../types'
import { toast } from '../lib/toast'
import { useAppStore } from '../lib/store'

interface BookingModalProps {
  booking: Booking | null
  isOpen: boolean
  onClose: () => void
  onUpdate: (booking: Booking) => void
  onDelete: (bookingId: string) => void
}

export default function BookingModal({ booking, isOpen, onClose, onUpdate, onDelete }: BookingModalProps) {
  const [isEditing, setIsEditing] = React.useState(false)
  const [editedBooking, setEditedBooking] = React.useState<Booking | null>(null)
  const { user } = useAppStore()

  React.useEffect(() => {
    if (booking) {
      setEditedBooking({ ...booking })
    }
  }, [booking])

  if (!isOpen || !booking) return null

  const handleSave = () => {
    if (editedBooking) {
      onUpdate(editedBooking)
      setIsEditing(false)
      toast.success('Booking updated successfully!')
    }
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this booking?')) {
      onDelete(booking.id)
      onClose()
      toast.success('Booking deleted successfully!')
    }
  }

  const handleStatusChange = (newStatus: Booking['status']) => {
    if (editedBooking) {
      setEditedBooking({ ...editedBooking, status: newStatus })
    }
  }

  const statusOptions: Booking['status'][] = ['pending', 'confirmed', 'completed', 'cancelled']

  const canEdit = booking.status !== 'completed' || user?.role === 'admin'

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:bg-dark-card rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-green-100 dark:border-dark-border">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-green-200 dark:border-gray-700 bg-green-50 dark:bg-gray-800 backdrop-blur-md">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-green-700 to-emerald-600 bg-clip-text text-transparent dark:text-gray-100">Booking Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200/50 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 bg-white dark:bg-gray-900">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Service Name</label>
                <input
                  type="text"
                  value={editedBooking?.service_name || ''}
                  onChange={(e) => setEditedBooking(editedBooking ? { ...editedBooking, service_name: e.target.value } : null)}
                  className="w-full px-3 py-2 border border-green-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:focus:ring-emerald-400 dark:focus:border-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Date</label>
                  <input
                    type="date"
                    value={editedBooking?.date || ''}
                    onChange={(e) => setEditedBooking(editedBooking ? { ...editedBooking, date: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-green-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:focus:ring-emerald-400 dark:focus:border-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Time</label>
                  <input
                    type="text"
                    value={editedBooking?.time || ''}
                    onChange={(e) => setEditedBooking(editedBooking ? { ...editedBooking, time: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-green-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:focus:ring-emerald-400 dark:focus:border-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Amount ($)</label>
                  <input
                    type="number"
                    value={editedBooking?.amount || 0}
                    onChange={(e) => setEditedBooking(editedBooking ? { ...editedBooking, amount: Number(e.target.value) } : null)}
                    className="w-full px-3 py-2 border border-green-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:focus:ring-emerald-400 dark:focus:border-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Status</label>
                  <select
                    value={editedBooking?.status || 'pending'}
                    onChange={(e) => handleStatusChange(e.target.value as Booking['status'])}
                    className="w-full px-3 py-2 border border-green-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:focus:ring-emerald-400 dark:focus:border-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {statusOptions.map(status => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Service Info */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-3">{booking.service_name}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">{booking.date}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">{booking.time}</span>
                  </div>
                </div>
              </div>

              {/* Status and Amount */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <DollarSign className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Amount</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">${booking.amount}</p>
                </div>

                <div className={`rounded-lg p-4 ${
                  booking.status === 'confirmed' ? 'bg-green-50 dark:bg-green-900/20' :
                  booking.status === 'pending' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                  booking.status === 'completed' ? 'bg-blue-50 dark:bg-blue-900/20' :
                  'bg-red-50 dark:bg-red-900/20'
                }`}>
                  <div className="flex items-center space-x-2 mb-2">
                    {booking.status === 'confirmed' ? <CheckCircle className="w-4 h-4 text-green-600" /> :
                     booking.status === 'pending' ? <AlertCircle className="w-4 h-4 text-yellow-600" /> :
                     <CheckCircle className="w-4 h-4 text-blue-600" />}
                    <span className={`text-sm font-medium ${
                      booking.status === 'confirmed' ? 'text-green-900 dark:text-green-100' :
                      booking.status === 'pending' ? 'text-yellow-900 dark:text-yellow-100' :
                      booking.status === 'completed' ? 'text-blue-900 dark:text-blue-100' :
                      'text-red-900 dark:text-red-100'
                    }`}>Status</span>
                  </div>
                  <p className={`text-lg font-semibold capitalize ${
                    booking.status === 'confirmed' ? 'text-green-600 dark:text-green-400' :
                    booking.status === 'pending' ? 'text-yellow-600 dark:text-yellow-400' :
                    booking.status === 'completed' ? 'text-blue-600 dark:text-blue-400' :
                    'text-red-600 dark:text-red-400'
                  }`}>{booking.status}</p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2">
                {statusOptions.map(status => (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    disabled={!canEdit}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-800/40' :
                      status === 'confirmed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800/40' :
                      status === 'completed' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800/40' :
                      'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/40'
                    } ${booking.status === status ? 'ring-2 ring-offset-2 ring-orange-500' : ''} ${
                      !canEdit ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-b-xl">
          <div className="flex space-x-2">
            {!isEditing && canEdit && (
              <button
                onClick={handleDelete}
                className="btn-crud-delete"
              >
                Delete
              </button>
            )}
          </div>

          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="btn-crud-cancel"
            >
              {isEditing ? 'Cancel' : 'Close'}
            </button>
            
            {isEditing ? (
              <button
                onClick={handleSave}
                className="btn-crud-edit"
              >
                Save Changes
              </button>
            ) : (
              canEdit && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-crud-edit"
                >
                  Edit
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
